import { Card, Flex, rem } from '@mantine/core';
import type { ComponentProps } from 'react';
import styled from 'styled-components';
import { asPlainComponent } from '@dealstack/frontend/shared/utils/asPlainComponent';

const PlainCard = asPlainComponent<ComponentProps<typeof Card>>(Card);

export const ParentDiv = styled.div`
	display: flex;
	flex-direction: column;
`;

export const HoldingCard = styled(PlainCard)`
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	border: none;
	border-radius: 4px 4px 0 0;
	display: flex;
	flex-direction: column;
	padding: 8px;
`;

export const SelectTitle = styled.div`
	font-size: 17px;
	font-weight: 400;
	line-height: 24px;
`;

export const HoldingsEventTabs = styled.div`
	display: flex;
	margin-top: 12px;
	background: #fff;
	border: 1px solid #e9ebed;
	border-radius: 6px;
	overflow: hidden;
	padding: 4px;
`;

export const HoldingsEventTab = styled.button.attrs({ type: 'button' })<{
	selected?: boolean;
	disabled?: boolean;
}>`
	flex: 1;
	border: none;
	background: transparent;
	padding: 8px;
	cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
	font-size: 14px;
	font-weight: 500;
	transition: all 0.2s ease-in-out;
	text-align: center;
	border-radius: 4px;
	opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
	display: flex;
	align-items: center;
	justify-content: center;

	&:hover {
		background: ${({ disabled }) => (disabled ? 'transparent' : 'rgba(0, 0, 0, 0.05)')};
	}

	${({ selected, disabled }) =>
		selected &&
		!disabled &&
		`
		background: #E7EFFE;
		box-shadow: 0 0 0 1px #0D5EF2;
	`}
`;

export const InfoCard = styled(PlainCard)`
	border-radius: 0 0 4px 4px;
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 8px;
	border: 1px solid #e9ebed;
`;

export const UnitsTitle = styled.div`
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
`;

export const DropZoneContainer = styled.div<{ isDragging?: boolean }>`
	border: 2px dashed ${(props) => (props.isDragging ? '#3b82f6' : '#BDC4C8')};
	border-radius: 8px;
	padding: 8px 16px;
	cursor: pointer;
	transition: all 0.3s ease;
	background-color: ${(props) => (props.isDragging ? '#f0f7ff' : 'transparent')};

	&:hover {
		border-color: #94a3b8;
	}
`;

export const DropZoneContent = styled.div`
	display: flex;
	padding-top: 16px;
	padding-bottom: 16px;
	gap: 16px;
	cursor: pointer;
`;

export const DropZoneFiles = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
`;

export const UploadText = styled.div`
	color: #4f616c;
	line-height: 20px;
	font-size: 14px;
	font-weight: 400;
`;

export const UploadLinkText = styled.span`
	color: #0d5ef2;
	font-weight: 600;
`;

export const FileTypeText = styled.div`
	font-size: 14px;
	color: #4f616c;
`;

export const FilePreviewContainer = styled.div`
	border: 1px solid #cbd5e1;
	border-radius: 4px;
	padding: 8px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: #e9ebed;
`;

export const FileInfoContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

export const FileName = styled.span`
	color: #0e171c;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
`;

export const TrashButton = styled.button`
	background: none;
	border: none;
	color: #ef4444;
	cursor: pointer;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;

	&:hover {
		background-color: #fee2e2;
	}
`;

export const EventCardsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-bottom: 16px;
`;

export const EventCardWrapper = styled.div`
	border: 1px solid #e9ebed;
	border-radius: 8px;
	overflow: hidden;
`;

export const EditFormCard = styled.div`
	border: 1px solid #e9ebed;
	border-radius: 8px;
	overflow: hidden;
	background-color: #ffffff;
`;

export const EventHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px;
	background-color: #f8f9fa;
	border-bottom: 1px solid #e9ebed;
`;

export const HeaderTitle = styled.div`
	margin: 0;
	font-size: 16px;
	font-weight: 500;
`;

export const EventContent = styled.div`
	padding: 16px;
`;

export const EventForm = styled.div`
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

export const EventField = styled.div`
	margin-bottom: 8px;
	&:last-child {
		margin-bottom: 0;
	}
`;

export const FieldLabel = styled.span`
	margin-right: 8px;
	font-size: 14px;
	font-style: normal;
	font-weight: 600;
	line-height: 20px;
`;

export const FieldValue = styled.span`
	word-break: break-word;
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;
`;

export const DocumentValue = styled.span`
	word-break: break-word;
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;
	cursor: pointer;
`;

export const EventDeleteCardWrapper = styled.div<{ isSelected: boolean }>`
	border: 1px solid ${({ isSelected }) => (isSelected ? '#f03e3e' : '#e9ebed')};
	border-radius: 8px;
	overflow: hidden;
`;

export const AlertBox = styled(Flex).attrs({ align: 'flex-start' })`
	padding: ${rem('16px')};
	border: 1px solid #fbb6c0;
	border-radius: 4px;
	gap: ${rem('12px')};
`;

export const AlertTitle = styled.div`
	font-size: ${rem('14px')};
	font-weight: 600;
	line-height: 20px;
`;

export const AlertSubtitle = styled.div`
	font-size: ${rem('14px')};
	color: #15232b;
`;

export const ConfirmationBox = styled.div`
	display: flex;
	justify-content: center;
	align-content: center;
	text-align: center;
	background-color: #f8f9fa;
	border-radius: 4px;
	padding: 16px;
	overflow: hidden;
	margin-bottom: 16px;
`;
